import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import { useTelegramLink } from '@/features/auth/telegram';
import { profileKeys } from '../api';

/**
 * Attaches Telegram to an account that was created with an email address.
 *
 * Its own card rather than a button on the account row: the exchange has three
 * steps and a code to read, and a row that grows a deep link, a four-digit code
 * and a waiting state would push everything under it around while somebody is
 * mid-flow.
 */
export function TelegramLinkCard() {
  const queryClient = useQueryClient();

  const { state, start, reset } = useTelegramLink(() => {
    // The profile now has a telegram block. Refetching is what makes the card
    // disappear and the account row fill in, without a reload.
    void queryClient.invalidateQueries({ queryKey: profileKeys.all });
  });

  const waiting = state.phase === 'pending' || state.phase === 'starting';

  return (
    <section className="rounded-[var(--radius-card)] border border-surface-800 bg-surface-900/60 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-surface-100">
        <TelegramGlyph />
        Telegram bağla
      </h2>

      <p className="mt-2 text-sm leading-relaxed text-surface-400">
        Hesabına Telegram ekleyince bir dahaki sefere parolasız girebilirsin.
      </p>

      {state.phase === 'failed' ? (
        <Alert tone="error" className="mt-4">
          {state.error}
        </Alert>
      ) : null}

      {state.phase === 'approved' ? (
        <Alert tone="success" className="mt-4">
          Telegram hesabın bağlandı.
        </Alert>
      ) : null}

      {state.phase === 'denied' || state.phase === 'expired' ? (
        <Alert tone="info" className="mt-4">
          {state.phase === 'denied'
            ? 'İstek Telegram’dan reddedildi.'
            : 'Süre doldu. Yeniden dene.'}
        </Alert>
      ) : null}

      {state.phase === 'pending' && state.verificationCode ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="rounded-xl border border-surface-700 bg-surface-950 p-4 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-surface-500">
              Doğrulama kodu
            </p>
            <p className="mt-1 font-mono text-3xl font-bold tracking-[0.3em] text-brand-400 tabular-nums">
              {state.verificationCode}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-surface-500">
              Botta bu kodu göreceksin. Aynı değilse onaylama — kodu eşleştirmek, senin linkine
              tıklamış birinin senin yerine giriş yapmasını engelleyen şey.
            </p>
          </div>

          {state.deepLink ? (
            <a
              href={state.deepLink}
              target="_blank"
              rel="noreferrer noopener"
              className={cn(
                'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-5',
                'bg-[#2aabee] text-sm font-semibold text-white transition hover:brightness-110',
              )}
            >
              <TelegramGlyph />
              Telegram’da aç
            </a>
          ) : null}

          <p className="text-center text-xs text-surface-500">Onayın bekleniyor…</p>

          <button
            type="button"
            onClick={reset}
            className="text-xs text-surface-500 underline-offset-4 transition hover:text-surface-300 hover:underline"
          >
            Vazgeç
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void start()}
          disabled={waiting}
          className={cn(
            'mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-5',
            'bg-[#2aabee] text-sm font-semibold text-white transition hover:brightness-110',
            'disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto',
          )}
        >
          <TelegramGlyph />
          {state.phase === 'starting' ? 'Hazırlanıyor…' : 'Telegram bağla'}
        </button>
      )}
    </section>
  );
}

function TelegramGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0 fill-current">
      <path d="M21.9 4.3 18.7 19.4c-.2 1-.9 1.3-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.8 8.7-7.9c.4-.3-.1-.5-.6-.2L6.7 13.2 2 11.7c-1-.3-1-1 .2-1.5l18.4-7.1c.9-.3 1.6.2 1.3 1.2z" />
    </svg>
  );
}
